import React, { useState, useMemo } from "react";
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useForm, SubmitHandler } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { AppDispatch } from "src/store";
import {
  forgotPasswordTalent,
  forgotPasswordCompany,
  forgotPasswordBackoffice,
} from "src/features/authSlice";
import { AccountType } from "src/types/firebase";
import { useTranslation } from "react-i18next";

interface ForgotPasswordFormInputs {
  email: string;
  accountType: string;
}

const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation();

  // Memoize schema - only recreate when language changes
  const schema = useMemo(
    () => Yup.object().shape({
      email: Yup.string()
        .email(t("auth.validation.invalidEmail"))
        .required(t("auth.validation.email")),
      accountType: Yup.string()
        .required(t("auth.validation.accountType"))
        .oneOf(["talent", "company", "backoffice"], t("auth.validation.invalidAccountType")),
    }),
    [t]
  );
  const resolver = useMemo(() => yupResolver(schema), [schema]);

  const [accountType, setAccountType] = useState<AccountType>("talent");
  const [success, setSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormInputs>({
    resolver: resolver as any,
    defaultValues: {
      accountType: "talent",
    },
  });

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { loading, error } = useSelector((state: any) => state.auth);

  const onSubmit: SubmitHandler<ForgotPasswordFormInputs> = async (data) => {
    setSuccess(false);

    // Call appropriate forgotPassword action based on accountType
    const result = await dispatch(
      data.accountType === "talent"
        ? forgotPasswordTalent(data.email)
        : data.accountType === "company"
        ? forgotPasswordCompany(data.email)
        : forgotPasswordBackoffice(data.email)
    );

    // Check if the action was successful
    if (
      result.type === "auth/forgotPasswordTalent/fulfilled" ||
      result.type === "auth/forgotPasswordCompany/fulfilled" ||
      result.type === "auth/forgotPasswordBackoffice/fulfilled"
    ) {
      setSuccess(true);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: "100%" }}>
          <Typography component="h1" variant="h5" align="center" gutterBottom>
            {t("auth.forgotPassword.title")}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && !error && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t("auth.forgotPassword.successMessage")}
            </Alert>
          )}

          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            sx={{ mt: 1 }}
          >
            <TextField
              {...register("email")}
              margin="normal"
              required
              fullWidth
              id="email"
              label={t("auth.forgotPassword.email")}
              name="email"
              autoComplete="email"
              autoFocus
              error={!!errors.email}
              helperText={errors.email?.message}
            />

            <FormControl fullWidth margin="normal" required>
              <InputLabel id="account-type-label">{t("auth.forgotPassword.accountType")}</InputLabel>
              <Select
                labelId="account-type-label"
                label={t("auth.forgotPassword.accountType")}
                value={accountType}
                {...register("accountType")}
                onChange={(e) => setAccountType(e.target.value as AccountType)}
              >
                <MenuItem value="talent">{t("auth.common.talent")}</MenuItem>
                <MenuItem value="company">{t("auth.common.company")}</MenuItem>
                <MenuItem value="backoffice">{t("auth.common.backoffice")}</MenuItem>
              </Select>
            </FormControl>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? t("auth.forgotPassword.sending") : t("auth.forgotPassword.sendButton")}
            </Button>

            <Box sx={{ mt: 2, textAlign: "center" }}>
              <Link
                component="button"
                variant="body2"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/login");
                }}
              >
                {t("auth.forgotPassword.backToLogin")}
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default ForgotPasswordPage;
